// lib/slides/noteMetrics.ts
// Typography scales with the sheet; noteLayout handles fitting and overflow.
import { layoutLineLimit, stampDisplayText, wrapStampText } from './stampLayout';
import { measureStandardText } from './standardText';

export const NOTE_FONT_PT = 8.5;
export const NOTE_LINE_STEP = 1.2; // line height as a multiple of font size
export const NOTE_PAD_PT = 6; // inner padding on every side
const NOTE_MIN_W = 140;
export const NOTE_MAX_W = 300;
export const NOTE_CORNER_RADIUS = 6;

export const noteDisplayText = (text: string): string => stampDisplayText(text.replace(/[^\S\r\n]+/g, ' '));

/** A4 portrait diagonal in points: the sheet the constants above were tuned on. */
const REFERENCE_DIAGONAL = Math.hypot(595, 842);
/** Below this a page is a slide, not a sheet; do not shrink callouts further. */
const MIN_NOTE_SCALE = 1;

/**
 * How much bigger this page is than A4, as a multiplier for anything drawn on
 * it at a size tuned on A4, such as callouts.
 * 1 on A4 in either orientation, ~1.4 on A3, ~4 on A0.
 *
 * Floored at 1: a 960x540 pitch slide is smaller than A4 by diagonal, but its
 * callouts are read on screen at the same distance as any other, and shrinking
 * them buys nothing.
 */
export const pageScaleFromA4 = (pageW?: number, pageH?: number): number => {
  const w = Number(pageW);
  const h = Number(pageH);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 1;
  return Math.max(MIN_NOTE_SCALE, Math.hypot(w, h) / REFERENCE_DIAGONAL);
};

export const measureNoteText = (text: string, size: number): number => measureStandardText(text, size);

/**
 * Exact-fit frame for a note's text, sized for the page it goes on. Height is
 * COMPUTED from the wrap, never guessed. Returns page-point w/h.
 *
 * The model's `desiredW` hint is scaled along with everything else. It writes
 * widths that look right on A4 because that is the size its examples are drawn
 * at, so honouring the hint literally on a large sheet reproduces the very bug
 * this scaling exists to remove.
 */
export async function noteFrameForText(
  text: string,
  desiredW?: number,
  pageW?: number,
  pageH?: number
): Promise<{ w: number; h: number; lines: number }> {
  const s = pageScaleFromA4(pageW, pageH);
  const hinted = (Number.isFinite(desiredW as number) ? (desiredW as number) : 230) * s;
  const w = Math.max(1, Math.min(NOTE_MAX_W * s, Math.max(NOTE_MIN_W * s, hinted), pageW ? pageW - 16 - 0.8 * s : Infinity));
  const fontPt = NOTE_FONT_PT * s;
  const padPt = NOTE_PAD_PT * s;
  const lines = wrapStampText(noteDisplayText(text ?? ''), fontPt, false, w - padPt * 2, measureNoteText,
    layoutLineLimit(pageH ?? 842, fontPt * NOTE_LINE_STEP));
  const h = Math.round((padPt * 2 + lines.length * fontPt * NOTE_LINE_STEP + 1.5 * s) * 10) / 10;
  return { w, h, lines: lines.length };
}
